import { Combobox } from '@/components'
import { PlainLink } from '@/components_deprecated/Link'
import type { UserNode } from '@/modules/users'
import type { FormOnChangeFn } from '@ur-solutions/react-hooks'
import { useTranslate } from '@ur-solutions/react-hooks'
import React from 'react'
import styled from 'styled-components'
import { IssueDateRangeField } from './issue-date-range-field'
import { IssueRiskAspectCombobox } from './risk-aspect-combobox'
import { useIssueProcessField } from './use-issue-process-field'
import { useIssueSeverityOptions } from '../consts'
import type { EditIssueForm } from '../types'
import type { UseIssueFieldConfig } from '../field-config'
import { IssueFormField } from '../field-config'

const Card = styled.section`
  border-radius: 16px;
  background: white;
  box-shadow: var(--shadow-card);

  /* On mobile the columns flatten (see the view's Content rule); lift this
     metadata card to sit directly after the title. */
  ${props => props.theme.media.custom({ max: '1100px' })} {
    order: -1;
  }
`

const Row: React.FC<
  React.PropsWithChildren<{ label: React.ReactNode; required?: boolean }>
> = ({ label, required, children }) => (
  <div className="space-y-1.5 border-t border-stone-200 px-5 py-3.5 first:border-t-0">
    <div className="flex items-center gap-1 text-xs font-semibold uppercase text-stone-500">
      {label}
      {required && <span className="text-rose-500">*</span>}
    </div>
    <div className="text-sm font-medium text-stone-900">{children}</div>
  </div>
)

interface CreateMainInfoProps {
  form: EditIssueForm
  updateForm: (values: Partial<EditIssueForm>) => void
  onChange: FormOnChangeFn<EditIssueForm>
  fieldConfig: UseIssueFieldConfig
  /** Risk aspects are only required when the company has defined any. */
  hasRiskAspects: boolean
  /**
   * Shown here, but set by the category picker at the head of the page — both
   * default from the picked category until the issue is saved.
   */
  responsibleUser: Pick<UserNode, 'id' | 'fullName'> | null
  caseHandler: Pick<UserNode, 'id' | 'fullName'> | null
}

export const CreateMainInfo: React.FC<CreateMainInfoProps> = ({
  form,
  updateForm,
  onChange,
  fieldConfig,
  hasRiskAspects,
  responsibleUser,
  caseHandler,
}) => {
  const translations = useTranslate({
    responsible: 'common.responsible',
    caseHandler: 'common.case-handler',
    date: 'common.date',
    severity: 'common.severity',
    riskAspect: 'common.risk-aspect',
    process: 'issues.process',
    none: 'common.none',
  })
  const severityOptions = useIssueSeverityOptions()
  const processField = useIssueProcessField(form.processChoice)

  return (
    <Card>
      {fieldConfig.isVisible(IssueFormField.SEVERITY, 'create') && (
        <Row
          label={fieldConfig.labelFor(
            IssueFormField.SEVERITY,
            translations.severity
          )}
          required={fieldConfig.isRequired(IssueFormField.SEVERITY, 'create')}
        >
          <Combobox<string>
            mode="single"
            value={form.severity ?? ''}
            items={severityOptions}
            clearable
            onChange={value =>
              onChange('severity')((value || null) as EditIssueForm['severity'])
            }
          />
        </Row>
      )}

      {fieldConfig.isVisible(IssueFormField.RISK_ASPECT, 'create') && (
        <Row
          label={fieldConfig.labelFor(
            IssueFormField.RISK_ASPECT,
            translations.riskAspect
          )}
          required={
            hasRiskAspects &&
            fieldConfig.isRequired(IssueFormField.RISK_ASPECT, 'create')
          }
        >
          <IssueRiskAspectCombobox
            value={form.riskAspect}
            onChange={onChange('riskAspect')}
          />
        </Row>
      )}

      {processField.hasProcesses &&
        fieldConfig.isVisible(IssueFormField.PROCESS_CHOICE, 'create') && (
          <Row
            label={fieldConfig.labelFor(
              IssueFormField.PROCESS_CHOICE,
              translations.process
            )}
            required={fieldConfig.isRequired(
              IssueFormField.PROCESS_CHOICE,
              'create'
            )}
          >
            <Combobox<string>
              mode="single"
              value={form.processChoice ?? ''}
              items={processField.options}
              clearable
              onChange={value => onChange('processChoice')(value || null)}
            />
          </Row>
        )}

      {fieldConfig.isVisible(IssueFormField.RESPONSIBLE_USER, 'create') && (
        <Row label={translations.responsible}>
          {responsibleUser ? (
            <PlainLink
              to={`/users/${responsibleUser.id}`}
              $hoverColor="secondary"
            >
              {responsibleUser.fullName}
            </PlainLink>
          ) : (
            <span className="text-stone-500">{translations.none}</span>
          )}
        </Row>
      )}

      {fieldConfig.isVisible(IssueFormField.CASE_HANDLER, 'create') && (
        <Row label={translations.caseHandler}>
          {caseHandler ? (
            <PlainLink to={`/users/${caseHandler.id}`} $hoverColor="secondary">
              {caseHandler.fullName}
            </PlainLink>
          ) : (
            <span className="text-stone-500">{translations.none}</span>
          )}
        </Row>
      )}

      <Row label={translations.date}>
        <IssueDateRangeField
          start={form.date}
          end={form.dateEnd}
          onChange={(date, dateEnd) => updateForm({ date, dateEnd })}
        />
      </Row>
    </Card>
  )
}
